import CreativeType from '@/Enums/Creatives/CreativeType.enum';
import CloseButton from '@/components/Modal/common/CloseButton';
import ModalBody from '@/components/Modal/common/ModalBody';
import { CREATIVE_TYPES_ICONS, CREATIVE_TYPES_TITLES } from '@/constants/creativeLibrary';
import { Chip, Modal } from '@mui/material';
import DisplayTagPreview from './components/DisplayTagPreview';
import FilePreview from './components/FilePreview';
import VastMediaPlayer from './components/VastMediaPlayer';

interface IProps {
  open: boolean;
  handleClose: () => void;
  name: string;
  type: CreativeType;
  /** Tag-backed creatives (VAST, display tag). */
  tagContent?: string | null;
  /** File-backed creatives (video, display, audio). */
  fileUrl?: string | null;
  /** Optional details strip, as the Creative Library's View modal shows them. */
  clickUrl?: string | null;
  size?: string | null;
}

const CreativePreviewModal = ({ open, handleClose, name, type, tagContent, fileUrl, clickUrl, size }: IProps) => {
  const notAvailable = <p className='text-gray-500'>Preview not available for this creative type.</p>;
  const TypeIcon = CREATIVE_TYPES_ICONS[type];
  const showDetails = Boolean(clickUrl || size);

  const renderPlayer = () => {
    switch (type) {
      case CreativeType.VIDEO_VAST:
        return tagContent ? <VastMediaPlayer tagContent={tagContent} mediaType='video' /> : notAvailable;
      case CreativeType.AUDIO_VAST:
        return tagContent ? <VastMediaPlayer tagContent={tagContent} mediaType='audio' /> : notAvailable;
      case CreativeType.DISPLAY_TAG:
        return tagContent ? <DisplayTagPreview tagContent={tagContent} /> : notAvailable;
      case CreativeType.VIDEO:
      case CreativeType.DISPLAY:
      case CreativeType.AUDIO:
        return fileUrl ? <FilePreview format={type} src={fileUrl} /> : notAvailable;
      default:
        return notAvailable;
    }
  };

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby='creative-preview-title'>
      <ModalBody sx={{ minWidth: 900, width: 960 }}>
        <div className='flex items-center justify-between mb-4'>
          <h2 id='creative-preview-title' className='font-semibold text-xl truncate pr-4'>
            {name}
          </h2>
          <CloseButton onClick={handleClose} />
        </div>
        {showDetails && (
          <div className='flex items-center gap-3 mb-4 text-sm text-gray-600'>
            {TypeIcon && (
              <span className='flex items-center'>
                <TypeIcon className='w-4 mr-1' />
                {CREATIVE_TYPES_TITLES[type]}
              </span>
            )}
            {size && <Chip variant='outlined' label={size} size='small' />}
            {clickUrl && (
              <a href={clickUrl} target='_blank' rel='noreferrer' className='truncate underline'>
                {clickUrl}
              </a>
            )}
          </div>
        )}
        {renderPlayer()}
      </ModalBody>
    </Modal>
  );
};

export default CreativePreviewModal;
